// revfxCatalogue.ts
// Single source of truth for the RevFX visual-effect picker, auto-generated prop
// controls, and per-effect lazy loaders. Metadata (props/summary/inputs) is read
// from the package's manifest.json; the loader map below is a STATIC thunk map so
// the bundler can code-split each effect into its own chunk and keep `three` out
// of every page except the WebGL effects that actually import it.
//
// NOTE: effect ids are the manifest `id` keys (e.g. 'sunbeam' → GodRays), NOT the
// component names. Always resolve the import path / component name via this module.
import manifestRaw from '@revfx/react/manifest.json';
import type { ComponentType } from 'react';

export interface RevfxPropMeta {
  type: string;
  default?: unknown;
  doc?: string;
}

export interface RevfxEffectMeta {
  id: string;
  component: string;       // named export inside the effect module
  importPath: string;      // manifest `import` field (informational)
  category: string;        // 'canvas2d' | 'dom-css' | 'webgl' | 'svg' | 'library-wrapper'
  peerDeps: string[];
  needsThree: boolean;     // derived from peerDeps, NOT category (fluiddynamics is
                           // webgl but has no three dep — deriving from category
                           // would wrongly force the three chunk onto it)
  needsLottie: boolean;
  summary: string;
  inputs: string;
  props: Record<string, RevfxPropMeta>;
}

// One literal import() per effect — required for reliable per-effect code-splitting
// (a dynamic import(variable) would NOT split). Each resolves the effect module;
// loadEffectComponent() then picks the named component export.
export const EFFECT_LOADERS: Record<string, () => Promise<Record<string, any>>> = {
  snow: () => import('@revfx/react/effects/canvas2d/Snow'),
  duotone: () => import('@revfx/react/effects/dom/Duotone'),
  polyfold: () => import('@revfx/react/effects/dom/PolyFold'),
  typewriter: () => import('@revfx/react/effects/dom/Typewriter'),
  revealer: () => import('@revfx/react/effects/dom/Revealer'),
  beforeafter: () => import('@revfx/react/effects/dom/BeforeAfter'),
  mousetrap: () => import('@revfx/react/effects/dom/MouseTrap'),
  filmstrip: () => import('@revfx/react/effects/dom/FilmStrip'),
  scrollvideo: () => import('@revfx/react/effects/canvas2d/ScrollVideo'),
  bubblemorph: () => import('@revfx/react/effects/canvas2d/BubbleMorph'),
  paintbrush: () => import('@revfx/react/effects/canvas2d/PaintbrushReveal'),
  slicey: () => import('@revfx/react/effects/dom/SliceyZoom'),
  particles: () => import('@revfx/react/effects/canvas2d/Particles'),
  explodinglayers: () => import('@revfx/react/effects/canvas2d/ExplodingReveal'),
  panorama: () => import('@revfx/react/effects/webgl/Panorama360'),
  distortion: () => import('@revfx/react/effects/webgl/DistortionImage'),
  sunbeam: () => import('@revfx/react/effects/webgl/GodRays'),
  fluiddynamics: () => import('@revfx/react/effects/webgl/FluidSimulation'),
  lottie: () => import('@revfx/react/effects/wrapper/LottiePlayer'),
  charts: () => import('@revfx/react/effects/svg/AnimatedChart'),
  carouselx: () => import('@revfx/react/effects/dom/CarouselX'),
  whiteboard: () => import('@revfx/react/effects/dom/Whiteboard'),
  transitionpack: () => import('@revfx/react/effects/webgl/ShaderTransition'),
  particlewave: () => import('@revfx/react/effects/webgl/ParticleWave'),
  hovermorph: () => import('@revfx/react/effects/webgl/HoverMorph'),
  shapeburst: () => import('@revfx/react/effects/webgl/ShapeBurst'),
  thecluster: () => import('@revfx/react/effects/webgl/GravityParticleField'),
};

const _manifest = manifestRaw as Record<string, any>;

export const REVFX_EFFECTS: RevfxEffectMeta[] = Object.keys(EFFECT_LOADERS).map((id) => {
  const m = _manifest[id] || {};
  const peerDeps: string[] = Array.isArray(m.peerDeps) ? m.peerDeps : [];
  return {
    id,
    component: m.component || id,
    importPath: m.import || '',
    category: m.category || 'other',
    peerDeps,
    needsThree: peerDeps.includes('three'),
    needsLottie: peerDeps.includes('lottie-web'),
    summary: m.summary || '',
    inputs: m.inputs || '',
    props: (m.props || {}) as Record<string, RevfxPropMeta>,
  };
});

export const REVFX_BY_ID: Record<string, RevfxEffectMeta> = Object.fromEntries(
  REVFX_EFFECTS.map((e) => [e.id, e])
);

// ── Per-effect integration model ────────────────────────────────────────────
// Most effects need inputs the generic "background layer" can't supply: a single
// image, an array of images, an image as CHILDREN (wrapper effects), a before/after
// image pair, or typed text. This map declares how the lp_effect renderer feeds
// each effect. Synthetic UI keys (prefixed `__`) are stored in effect_props and
// translated by the renderer into children / props before mounting the effect.
export type EffectRenderKind = 'background' | 'childImage' | 'beforeAfter' | 'text';

export interface EffectIntegration {
  kind: EffectRenderKind;       // how the renderer mounts it (default 'background')
  imageProps?: string[];        // string props that are a single image URL → image picker
  imageArrayProps?: string[];   // props that are an array of image URLs (string[]) → ImageArrayField
  imageItemsProps?: string[];   // props that are media-item objects ({src,type}[]) → ImageArrayField via __items
}

// Effects that cannot work as a hero "background + overlay" because they need
// structured data / slide children / a Lottie JSON file. Hidden from the picker.
// scrollvideo is a scroll-scrubbed image sequence — it needs a full-page scroll
// track and only shows one frame in a fixed section (it does NOT auto-cycle; use
// ShaderTransition for cycling images). Hidden to avoid the confusion.
export const UNSUPPORTED_EFFECTS = new Set<string>(['charts', 'carouselx', 'lottie', 'scrollvideo']);

export const EFFECT_INTEGRATION: Record<string, EffectIntegration> = {
  // Background effects that take a single image via a (sometimes oddly-named) prop.
  duotone:        { kind: 'background', imageProps: ['src'] },
  slicey:         { kind: 'background', imageProps: ['src'] },
  bubblemorph:    { kind: 'background', imageProps: ['image'] },
  paintbrush:     { kind: 'background', imageProps: ['image'] },
  sunbeam:        { kind: 'background', imageProps: ['source'] },        // GodRays
  distortion:     { kind: 'background', imageProps: ['src', 'displacementMap'] },
  panorama:       { kind: 'background', imageProps: ['src'] },
  shapeburst:     { kind: 'background', imageProps: ['src'] },
  lottie:         { kind: 'background', imageProps: ['src'] },           // Lottie JSON URL
  // Background effects that take an array of images.
  transitionpack: { kind: 'background', imageArrayProps: ['images'] },   // ShaderTransition (2+)
  scrollvideo:    { kind: 'background', imageArrayProps: ['frames'] },   // image sequence
  filmstrip:      { kind: 'background', imageItemsProps: ['items'] },    // {src,type}[] media strip
  // Wrapper effects: the image is their CHILDREN.
  revealer:        { kind: 'childImage' },
  mousetrap:       { kind: 'childImage' },
  explodinglayers: { kind: 'childImage' },                              // ExplodingReveal
  // Two-image comparison slider.
  beforeafter:    { kind: 'beforeAfter' },
  // Typed-text effect.
  typewriter:     { kind: 'text' },
};

export function effectIntegration(id: string): EffectIntegration {
  return EFFECT_INTEGRATION[id] || { kind: 'background' };
}

/** True if propKey is a single-image input for this effect (→ image picker). */
export function isEffectImageProp(id: string, propKey: string): boolean {
  return !!EFFECT_INTEGRATION[id]?.imageProps?.includes(propKey);
}

/** True if propKey is an image-array input for this effect (→ ImageArrayField). */
export function isEffectImageArrayProp(id: string, propKey: string): boolean {
  return !!EFFECT_INTEGRATION[id]?.imageArrayProps?.includes(propKey);
}

/** True if propKey is a media-items input ({src,type}[]) for this effect. */
export function isEffectImageItemsProp(id: string, propKey: string): boolean {
  return !!EFFECT_INTEGRATION[id]?.imageItemsProps?.includes(propKey);
}

/** Effects shown in the picker (excludes UNSUPPORTED_EFFECTS). */
export const REVFX_PALETTE: RevfxEffectMeta[] = REVFX_EFFECTS.filter((e) => !UNSUPPORTED_EFFECTS.has(e.id));

/** Whether an effect needs image input(s) and whether they have been supplied —
 *  used to show an editor placeholder prompting upload before the effect can paint. */
export function effectImageState(id: string, props: Record<string, any>): { needs: boolean; has: boolean } {
  const integ = EFFECT_INTEGRATION[id];
  const p = props || {};
  if (!integ) return { needs: false, has: true };
  if (integ.imageProps?.length) return { needs: true, has: !!p[integ.imageProps[0]] };
  if (integ.imageArrayProps?.length) {
    const arr = p[integ.imageArrayProps[0]];
    return { needs: true, has: Array.isArray(arr) && arr.filter(Boolean).length > 0 };
  }
  if (integ.imageItemsProps?.length) {
    return { needs: true, has: Array.isArray(p.__items) && p.__items.filter(Boolean).length > 0 };
  }
  if (integ.kind === 'childImage') return { needs: true, has: !!p.__img };
  if (integ.kind === 'beforeAfter') return { needs: true, has: !!p.__before && !!p.__after };
  return { needs: false, has: true };
}

/** The primary image URL an effect was given (for a static fallback if it fails to
 *  paint — e.g. a WebGL/driver failure). '' if the effect has no image input. */
export function effectPrimaryImage(id: string, effectProps: Record<string, any>): string {
  const integ = EFFECT_INTEGRATION[id];
  if (!integ || !effectProps) return '';
  if (integ.imageProps?.length) return (effectProps[integ.imageProps[0]] as string) || '';
  if (integ.kind === 'childImage') return (effectProps.__img as string) || '';
  if (integ.kind === 'beforeAfter') return (effectProps.__before as string) || '';
  return '';
}

/** Lazily load an effect's React component by manifest id, or null if unknown. */
export function loadEffectComponent(id: string): Promise<ComponentType<any> | null> {
  const loader = EFFECT_LOADERS[id];
  const meta = REVFX_BY_ID[id];
  if (!loader || !meta) return Promise.resolve(null);
  return loader().then((mod) => (mod[meta.component] as ComponentType<any>) || null);
}

/** Coerce a manifest `default` value: parse stringified array/object literals
 *  (e.g. "['a','b']" → ['a','b']) so effects get real values, not doc-strings. */
function coerceManifestDefault(type: string, def: any): any {
  if (typeof def === 'string') {
    const s = def.trim();
    if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) {
      try {
        // JS-literal → JSON: single→double quotes AND quote unquoted object keys
        // (the manifest stores e.g. lightCenter default as "{ x: 0.5, y: 0.3 }").
        const json = s.replace(/'/g, '"').replace(/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g, '$1"$2":');
        return JSON.parse(json);
      } catch { return type.endsWith(']') ? [] : {}; }
    }
  }
  return def;
}

/** Build the default props object for an effect from its manifest prop metadata.
 *  Props with a manifest default get it. Required array-typed props (no default,
 *  type ends in `]`, e.g. `string[]`, `number[]`, `{…}[]`) are seeded to `[]` so
 *  effects that read `.length` (Typewriter.phrases, FilmStrip.items, Charts.data,
 *  FluidSimulation.colors, …) don't throw on undefined before the user supplies
 *  content. (The effect error boundary is the ultimate safety net.) */
// Per-effect default overrides applied on top of the manifest defaults, to make an
// effect behave sensibly as a standalone lp_effect section.
const EFFECT_DEFAULT_OVERRIDES: Record<string, Record<string, any>> = {
  // PolyFold is normally a scroll-linked edge decoration that's flat (invisible)
  // when the section is fully in view. As a standalone section, show a static fold.
  polyfold: { scrollAnimate: false, height: 120 },
  // Whiteboard hand-draws its children; seed some text so it isn't blank on add.
  whiteboard: { __text: 'Your text here' },
};

export function defaultEffectProps(id: string): Record<string, any> {
  const meta = REVFX_BY_ID[id];
  if (!meta) return {};
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(meta.props)) {
    if (!v || typeof v !== 'object') continue;
    const t = typeof (v as any).type === 'string' ? (v as any).type.trim() : '';
    if ('default' in v) {
      // Some manifest defaults are stringified literals (e.g. shapes default is the
      // STRING "['circle','square']"); parse those into real arrays/objects so the
      // effect doesn't get a string where it expects an array (.join/.map crashes).
      out[k] = coerceManifestDefault(t, (v as any).default);
    } else if (t.endsWith(']')) {
      out[k] = []; // array-typed required prop → empty array (avoid .length crashes)
    } else if (t.includes('|')) {
      // string-literal union with no default → seed the first option so the effect
      // gets a real value matching what the picker shows (e.g. Duotone.preset).
      const m = t.split('|')[0].trim().match(/^'([^']*)'$|^"([^"]*)"$/);
      if (m) out[k] = m[1] ?? m[2];
    }
  }
  return { ...out, ...(EFFECT_DEFAULT_OVERRIDES[id] || {}) };
}
